import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "@/pages/settings/components/dealsSettings/FieldManagement/components/DrawerTitle";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { useUpdateOnboardingWorkflow } from "@/pages/settings/components/contractsSettings/hooks";
import {
  OnboardingStage,
  OnboardingStageWorkflow
} from "@/pages/settings/components/contractsSettings/types";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { mapOnboardingWorkflowType } from "./helpers";

const TRANSLATION_PREFIX =
  "settings.contracts.onboardingStudio.workflowDetail.editWorkflowDetails";

export type confirmationModalType =
  | "moveWorkflowForward"
  | "moveWorkflowBackward";

interface WorkflowDetailsFormValues {
  workflowName: string;
  workflowDescription: string;
  stage: string;
  workflowType: string;
}

interface EditOnboardingWorkflowDetailsDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workflow?: OnboardingStageWorkflow | null;
  stages: OnboardingStage[];
  onSave?: (workflow: OnboardingStageWorkflow) => void;
  onRemoveFromStage?: () => void;
}

const EditOnboardingWorkflowDetailsDrawer: React.FC<
  EditOnboardingWorkflowDetailsDrawerProps
> = ({ isOpen, onOpenChange, workflow, stages, onSave, onRemoveFromStage }) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const updateWorkflow = useUpdateOnboardingWorkflow();

  const stageId = workflow?.contractsOnboardingStageId;

  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formSetValue, setFormSetValue] = useState<
    ((name: string, value: string) => void) | null
  >(null);
  const [formGetValues, setFormGetValues] = useState<
    (() => WorkflowDetailsFormValues) | null
  >(null);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingStageChangeData, setPendingStageChangeData] = useState<{
    originalStageId: string;
    newStageId: string;
    modalType: confirmationModalType;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (workflow && formSetValue) {
      formSetValue("workflowName", workflow.name || "");
      formSetValue("workflowDescription", workflow.description || "");
      formSetValue("stage", workflow.contractsOnboardingStageId || "");
      formSetValue(
        "workflowType",
        String(workflow.workflowType ?? "")
      );
    }
  }, [workflow, formSetValue]);

  const handleClose = () => {
    setIsFormValid(false);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!isFormValid || !workflow?.id || !formGetValues) return;

    setIsSubmitting(true);
    try {
      const formValues = formGetValues();
      const result = await updateWorkflow.mutateAsync({
        pathParams: { workflowId: workflow.id },
        data: {
          name: formValues.workflowName?.trim(),
          description: formValues.workflowDescription || null,
          workflowType: workflow.workflowType,
          contractsOnboardingStageId: formValues.stage
        }
      });

      onSave?.(result);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const stageOptions = useMemo(
    () =>
      stages.map((stage) => ({
        id: stage.id,
        displayName: stage.name
      })),
    [stages]
  );

  const workflowTypeOptions = useMemo(() => {
    const type = mapOnboardingWorkflowType(workflow?.workflowType);
    return [
      {
        id: String(workflow?.workflowType ?? ""),
        displayName: t(
          `settings.deals.workflow.workflowDetailedView.workflowTypes.${type}`
        )
      }
    ];
  }, [workflow?.workflowType, t]);

  const getStageMovementType = (
    currentStageId: string,
    newStageId: string
  ): confirmationModalType => {
    const currentStage = stages.find((stage) => stage.id === currentStageId);
    const newStage = stages.find((stage) => stage.id === newStageId);

    if (!currentStage || !newStage) return "moveWorkflowForward";

    return newStage.itemOrder > currentStage.itemOrder
      ? "moveWorkflowForward"
      : "moveWorkflowBackward";
  };

  const handleStageSelection = (newStageId: string) => {
    const currentStageId = stageId || "";

    if (currentStageId === newStageId) {
      return;
    }

    const modalType = getStageMovementType(currentStageId, newStageId);

    setPendingStageChangeData({
      originalStageId: currentStageId,
      newStageId,
      modalType,
      onConfirm: () => {
        if (formSetValue) {
          formSetValue("stage", newStageId);
        }
      }
    });
    setIsConfirmationModalOpen(true);

    setTimeout(() => {
      if (formSetValue) {
        formSetValue("stage", currentStageId);
      }
    }, 0);
  };

  const handleConfirmStageChange = () => {
    if (pendingStageChangeData) {
      pendingStageChangeData.onConfirm();
      setPendingStageChangeData(null);
    }
    setIsConfirmationModalOpen(false);
  };

  const handleCancelStageChange = () => {
    if (pendingStageChangeData && formSetValue) {
      formSetValue("stage", pendingStageChangeData.originalStageId);
    }
    setPendingStageChangeData(null);
    setIsConfirmationModalOpen(false);
  };

  const formSections: FormSectionProps[] = [
    {
      id: "workflow-details",
      divider: true,
      sectionClassName: "gap-6",
      dividerClassName: "border-none",
      fields: [
        {
          name: "workflowName",
          type: "text" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowName`),
          required: true,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_PREFIX}.workflowNamePlaceholder`)
          }
        },
        {
          name: "workflowDescription",
          type: "textarea" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowDescription`),
          required: false,
          fieldActionProps: {
            placeholder: t(
              `${TRANSLATION_PREFIX}.workflowDescriptionPlaceholder`
            ),
            variant: "form",
            minRows: 1
          }
        },
        {
          name: "stage",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.stage`),
          required: true,
          fieldActionProps: {
            fieldName: "stage",
            options: stageOptions,
            placeholder: t(`${TRANSLATION_PREFIX}.stagePlaceholder`),
            onSelection: handleStageSelection
          }
        },
        {
          name: "workflowType",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowType`),
          required: true,
          fieldActionProps: {
            fieldName: "workflowType",
            options: workflowTypeOptions,
            disabled: true
          }
        }
      ] as FormFieldNewType[]
    }
  ];

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
        direction="right"
        modal={false}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <CustomDrawerTitle title={t(`${TRANSLATION_PREFIX}.title`)} />
            </DrawerTitle>
            <IconButton
              data-test="close-edit-onboarding-workflow-details-drawer"
              onClick={handleClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={(formMethods) => {
                setFormSetValue(() => formMethods.setValue);
                setFormGetValues(() => formMethods.getValues);
              }}
            />
          </div>

          <DrawerFooter>
            <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                variant="tertiary"
                onClick={onRemoveFromStage}
                className="h-9 text-red-600 hover:text-red-700"
              >
                {t(`${TRANSLATION_PREFIX}.removeFromStage`)}
              </Button>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="secondaryNoBg"
                  onClick={handleClose}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.cancel`)}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.saveChanges`)}
                </Button>
              </div>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      {pendingStageChangeData ? (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          title={t(
            `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.title`
          )}
          confirmationText={
            <div className="flex flex-col gap-4">
              <span>
                {t(
                  `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.message`
                )}
              </span>
              <span>
                {t(
                  `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.message2`
                )}
              </span>
            </div>
          }
          handleSubmit={handleConfirmStageChange}
          handleCancel={handleCancelStageChange}
          isPrimaryButton={true}
          submitButtonText={t(
            `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.confirm`
          )}
        />
      ) : null}
    </>
  );
};

export default EditOnboardingWorkflowDetailsDrawer;
